import { CenterSpinner } from '@/components/ats';
import { PDFReaderBrowser } from '@/components/file-viewers/reactPdf';
import { KText } from '@/ui-library/components';
import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { useEffect, useState, useRef } from 'react';
import { useGenerateOfferDocumentMutation, offerDetailsAPI } from '../../api';
import { useShowToast } from '@/hooks';
import { useOfferStore } from '../../store';
interface DocumentPreviewProps {
  offerId: string;
  templateId: string | null;
  hasValidationIssues: boolean;
  onRegenerateTrigger: () => void;
  regenerateKey: number;
  isTemplateSelectionComplete?: boolean;
  isMultiDoc?: boolean;
  selectedDocIndex?: number;
  // Wires the viewer's built-in "Replace" control to the offer's replace flow.
  onReplace?: (_files: File[]) => void;
  fileName?: string;
  showReplace?: boolean;
  onGenerationError?: (_docIndex: number) => void;
}

interface OfferDocumentPdf {
  url: string;
  content_type: string;
}

interface OfferData {
  offer_status: string;
  offer_document_pdf?: OfferDocumentPdf;
  // Per-document PDFs (multi-doc), index-aligned with the template's offer_documents tabs.
  offer_documents_pdf?: OfferDocumentPdf[];
}

// Manual polling function matching pollOfferWithRetries from offerFormHelpers
const pollOfferWithRetries = async (
  offerId: string,
  maxRetries = 15,
  timeout = 3000
): Promise<OfferData> => {
  let retries = 0;
  while (retries < maxRetries) {
    try {
      const response = await offerDetailsAPI.pollOfferById(offerId);
      if (response) {
        const status = response.offer_status;
        if (status === 'document_generated' || status === 'approved_internal') {
          return response;
        } else if (status === 'document_generation_error') {
          throw new Error('Document generation error');
        } else if (status !== 'pending_document_generation') {
          throw new Error('Unexpected status');
        }
      }
    } catch (err: any) {
      if (
        err.message === 'Document generation error' ||
        err.message === 'Unexpected status' ||
        retries === maxRetries - 1
      ) {
        throw err;
      }
    }
    retries += 1;
    await new Promise((res) => setTimeout(res, timeout));
  }
  throw new Error('Maximum retries reached');
};

export const DocumentPreview = ({
  offerId,
  templateId,
  hasValidationIssues,
  onRegenerateTrigger,
  regenerateKey,
  isTemplateSelectionComplete = true,
  isMultiDoc = false,
  selectedDocIndex = 0,
  onReplace,
  fileName,
  showReplace = false,
  onGenerationError,
}: DocumentPreviewProps) => {
  const isPolling = useOfferStore((state) => state.isPolling);
  const setIsPolling = useOfferStore((state) => state.setIsPolling);
  const setEnableNextButton = useOfferStore(
    (state) => state.setEnableNextButton
  );
  const [offerData, setOfferData] = useState<OfferData | null>(null);
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  // Identity of the in-flight generation; blocks duplicate runs for the same
  // template while a generation is still pending (multi-doc gate oscillation).
  const inFlightRef = useRef<string | null>(null);
  // Tracks the last successfully-completed run key so tab switches that flip
  // hasValidationIssues don't restart a generation that already finished.
  const completedKeyRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);
  const generateDocument = useGenerateOfferDocumentMutation();
  const toast = useShowToast();

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const offerStatus = offerData?.offer_status;
  // Multi-doc previews the selected tab's PDF; single-doc uses the legacy single PDF.
  const selectedPdf =
    isMultiDoc && offerData?.offer_documents_pdf?.length
      ? offerData.offer_documents_pdf[selectedDocIndex]
      : offerData?.offer_document_pdf;
  const pdfUrl = selectedPdf?.url || '';
  const contentType = selectedPdf?.content_type || '';

  // Combined effect: Generate document and poll if needed
  useEffect(() => {
    if (
      !offerId ||
      !templateId ||
      hasValidationIssues ||
      !isTemplateSelectionComplete
    ) {
      return;
    }

    // Skip if a generation for this same identity is already in-flight or has
    // already completed; the multi-doc gate can flip hasValidationIssues across
    // renders (tab switch) and would otherwise restart a finished generation.
    const runKey = `${offerId}:${templateId}:${regenerateKey}`;
    if (inFlightRef.current === runKey) return;
    if (completedKeyRef.current === runKey) return;
    inFlightRef.current = runKey;

    const generateAndPoll = async () => {
      setIsPolling(true);
      try {
        // Step 1: Generate document
        const result = await generateDocument.mutateAsync({
          id: offerId,
          event: 'generate_document',
        });

        if (!isMountedRef.current) return; // Don't update if unmounted

        const status = result?.offer_status;

        // Step 2: Check if we need to poll
        if (status === 'pending_document_generation') {
          // Poll for completion
          const polledData = await pollOfferWithRetries(offerId);
          if (isMountedRef.current) {
            setOfferData(polledData);
            completedKeyRef.current = runKey;
          }
        } else {
          // Document is already ready or in another state
          if (isMountedRef.current) {
            setOfferData(result);
            completedKeyRef.current = runKey;
          }
        }
      } catch (err: any) {
        if (!isMountedRef.current) return;

        toast({
          title: 'Error generating offer',
          description: err.message || 'Failed to generate offer document',
          status: 'error',
        });
        setOfferData({
          offer_status: 'document_generation_error',
        });
        onGenerationError?.(selectedDocIndex);
      } finally {
        // Clear only if this run still owns the ref, so a superseding run isn't reopened.
        if (inFlightRef.current === runKey) {
          inFlightRef.current = null;
        }
        if (isMountedRef.current) {
          setIsPolling(false);
        }
      }
    };

    generateAndPoll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    offerId,
    templateId,
    regenerateKey,
    hasValidationIssues,
    isTemplateSelectionComplete,
  ]);

  const isGenerating =
    generateDocument.isPending ||
    isPolling ||
    offerStatus === 'pending_document_generation' ||
    offerStatus === 'document_generation_in_progress';

  const showPreview =
    pdfUrl &&
    !isGenerating &&
    !hasValidationIssues &&
    offerStatus !== 'document_generation_error';

  const showError =
    !isGenerating && offerStatus === 'document_generation_error';

  useEffect(() => {
    if (isGenerating || showError) {
      setEnableNextButton(false);
    } else {
      setEnableNextButton(true);
    }
  }, [isGenerating, setEnableNextButton, showError]);

  // Measure container height
  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        const height = containerRef.current.offsetHeight;
        setContainerHeight(height);
      }
    };

    updateHeight();

    // Update height on window resize
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [showPreview]);

  // Don't render anything if there are validation issues
  if (hasValidationIssues) {
    return null;
  }

  return (
    <Box h="calc(100% - 120px)" w="100%">
      {/* Loading State */}
      {isGenerating && (
        <Flex
          mt="12px"
          direction="column"
          align="center"
          justify="center"
          minH="400px"
          border="1px solid"
          borderColor="border-color"
          borderRadius="sm"
        >
          <CenterSpinner h="100%" />
          <KText mt="12px" color="secondary-600">
            Generating offer document...
          </KText>
        </Flex>
      )}

      {/* Error State */}
      {showError && (
        <Flex
          mt="24px"
          direction="column"
          align="center"
          justify="center"
          minH="400px"
          border="1px solid"
          borderColor="border-color"
          borderRadius="sm"
        >
          <Text mb={4}>Document generation failed</Text>
          <Button variant="primary" onClick={onRegenerateTrigger}>
            Re-generate Offer
          </Button>
        </Flex>
      )}

      {/* PDF Preview */}
      {showPreview && (
        <Flex mt="12px" direction="column" gap="12px" h="100%">
          <Box
            ref={containerRef}
            overflow="hidden"
            h="100%"
            minH="300px"
            className="document-preview"
          >
            <PDFReaderBrowser
              file={pdfUrl}
              content_type={contentType}
              preview_url={null}
              showReplaceControl={!!showReplace}
              handleUpload={onReplace}
              acceptedFiles={['.docx']}
              maxFiles={1}
              fileName={fileName}
              containerStyles={{
                h:
                  containerHeight > 0
                    ? `${containerHeight}px`
                    : 'calc(100% - 400px)',
              }}
            />
          </Box>
        </Flex>
      )}
    </Box>
  );
};
